import { INSPIRATIONS_RESULTS_GRID_CLASS } from '../inspirations/constants';
import {
    LISTING_RESULTS_GRID_CLASS,
    LISTING_PRODUCT_WRAPPER_CLASS,
} from './constants';
import { escapeHtml } from '../helpers';

export const listingRendererMethods = {
    buildActiveFilterChipsFromState(filters = {}) {
        const chips = [];

        Object.entries(filters).forEach(([key, value]) => {
            const values = Array.isArray(value) ? value : [value];

            values.forEach(rawValue => {
                if (!rawValue) {
                    return;
                }

                const selector = `[data-listing-filter-select][data-taxonomy="${key}"][data-term-slug="${rawValue}"]`;
                const button =
                    this.getFilterUiRoots()
                        .map(root => root.querySelector(selector))
                        .find(node => node) || this.root.querySelector(selector);
                const labelNode = button?.querySelector(
                    '.listing__filter-label'
                );
                const labelText = labelNode?.textContent || '';
                const termName =
                    this.bootstrap.termIndex?.[key]?.[rawValue]?.name || '';
                const label =
                    labelText.replace(/\s*\([\d\s.]+\)\s*$/, '').trim() ||
                    termName ||
                    rawValue;

                chips.push({
                    key,
                    value: label,
                    raw_value: rawValue,
                });
            });
        });

        return chips;
    },

    renderListing(data) {
        if (this.title && Object.prototype.hasOwnProperty.call(data, 'title')) {
            this.title.textContent = data.title || '';
        }

        if (
            this.description &&
            Object.prototype.hasOwnProperty.call(data, 'description')
        ) {
            this.description.innerHTML = data.description || '';
        }

        if (this.total && Object.prototype.hasOwnProperty.call(data, 'total')) {
            this.total.textContent = this.formatTotal(data.total || 0);
        }

        if (
            Object.prototype.hasOwnProperty.call(data, 'navigationTerms') ||
            Object.prototype.hasOwnProperty.call(data, 'parentTerm')
        ) {
            this.renderTerms(
                data.navigationTerms || [],
                data.parentTerm || null
            );
        }

        if (Object.prototype.hasOwnProperty.call(data, 'activeFilterChips')) {
            this.renderFilters(data.activeFilterChips || []);
        }

        this.renderResults(data.results || [], {
            ...(data.pagination || {}),
            total: Number(data.total || 0),
        });
        this.updateSortUi(data.sort || this.state.sort);

        if (data.taxonomy) {
            this.root.dataset.taxonomy = data.taxonomy;
        }

        if (data.term?.id) {
            this.root.dataset.termId = String(data.term.id);
        }

        if (data.filtersSidebarHtml && this.filtersSidebar?.content) {
            this.filtersSidebar.content.innerHTML = data.filtersSidebarHtml;
            this.filtersSidebarContent =
                this.filtersSidebar.content.querySelector(
                    '[data-listing-filters-sidebar-content]'
                );
            this.filterCountsLoading = false;
            this.filterCountsLoaded = this.hasFilterCountsInSidebar();
        }

        this.syncPendingFiltersFromState();

        const applyLabel = this.getFiltersApplyLabelElement();

        if (applyLabel && Object.prototype.hasOwnProperty.call(data, 'total')) {
            applyLabel.textContent = this.formatFiltersApplyLabel(data.total);
        }
    },

    renderTerms(terms, parentTerm) {
        if (!this.termsSection || !this.termsTrack || !this.termsTrackWrapper) {
            return;
        }

        if (this.termsBackSlot) {
            this.termsBackSlot.innerHTML = parentTerm?.url
                ? `<a class="listing__terms-back" href="${
                      parentTerm.url
                  }" data-listing-term-back data-term-id="${
                      parentTerm.id || ''
                  }" data-taxonomy="${parentTerm.taxonomy || ''}">Cofnij</a>`
                : '';
        }

        const itemsMarkup = terms
            .map(term => {
                const activeClass = term.isActive ? ' is-active' : '';
                const current = term.isActive ? ' aria-current="page"' : '';
                const image = term.image?.url
                    ? `<img class="aspect-square w-[128px] object-cover border border-solid ${
                          term.isActive
                              ? 'border-orange-500'
                              : 'border-transparent'
                      } rounded-small hover:border-orange-500 transition-colors" src="${
                          term.image.url
                      }" alt="${
                          term.image.alt || term.name || ''
                      }" loading="lazy">`
                    : '';

                return `<div class="w-[194px] min-w-[194px] max-w-[194px] shrink-0" data-listing-term-card><a class="flex flex-col items-center gap-12 px-16 py-32${activeClass}" href="${term.url}" data-listing-term data-term-id="${term.id}" data-taxonomy="${term.taxonomy}"${current}>${image}<span class="block font-medium text-1413/[140%] text-center">${term.name}</span></a></div>`;
            })
            .join('');

        this.termsTrackWrapper.innerHTML = itemsMarkup;
        this.initTermsSlider();
    },

    initTermsSlider() {
        const carousels = this.root.querySelectorAll(
            '[data-listing-terms-carousel]'
        );

        if (carousels.length === 0) {
            return;
        }

        carousels.forEach(carousel => {
            const scroller = carousel.querySelector(
                '[data-listing-terms-scroller]'
            );

            if (!scroller) {
                return;
            }

            if (!scroller.dataset.listingTermsBound) {
                scroller.addEventListener(
                    'scroll',
                    () => this.updateCarouselControls(carousel),
                    { passive: true }
                );
                scroller.dataset.listingTermsBound = 'true';
            }

            this.updateCarouselControls(carousel);
        });
    },

    onWindowResize() {
        this.root
            .querySelectorAll('[data-listing-terms-carousel]')
            .forEach(carousel => this.updateCarouselControls(carousel));
    },

    scrollTermsTrack(carousel, direction) {
        const scroller = carousel?.querySelector(
            '[data-listing-terms-scroller]'
        );
        const cards = Array.from(
            carousel?.querySelectorAll('[data-listing-term-card]') || []
        );

        if (!scroller || cards.length === 0) {
            return;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const targetCard =
            direction > 0
                ? cards.find(
                      card =>
                          card.getBoundingClientRect().right >
                          scrollerRect.right + 1
                  )
                : [...cards]
                      .reverse()
                      .find(
                          card =>
                              card.getBoundingClientRect().left <
                              scrollerRect.left - 1
                      );

        if (!targetCard) {
            this.updateCarouselControls(carousel);
            return;
        }

        const targetRect = targetCard.getBoundingClientRect();

        scroller.scrollTo({
            left: scroller.scrollLeft + (targetRect.left - scrollerRect.left),
            behavior: 'smooth',
        });

        window.requestAnimationFrame(() =>
            this.updateCarouselControls(carousel)
        );
        window.setTimeout(() => this.updateCarouselControls(carousel), 250);
    },

    updateCarouselControls(carousel) {
        const scroller = carousel?.querySelector(
            '[data-listing-terms-scroller]'
        );
        const cards = Array.from(
            carousel?.querySelectorAll('[data-listing-term-card]') || []
        );
        const prevButton = carousel?.querySelector('[data-listing-terms-prev]');
        const nextButton = carousel?.querySelector('[data-listing-terms-next]');

        if (!scroller || !prevButton || !nextButton) {
            return;
        }

        if (cards.length === 0) {
            this.toggleTermsScrollButton(prevButton, false);
            this.toggleTermsScrollButton(nextButton, false);
            return;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const canScrollPrev = cards.some(
            card => card.getBoundingClientRect().left < scrollerRect.left - 1
        );
        const canScrollNext = cards.some(
            card => card.getBoundingClientRect().right > scrollerRect.right + 1
        );

        this.toggleTermsScrollButton(prevButton, canScrollPrev);
        this.toggleTermsScrollButton(nextButton, canScrollNext);
    },

    toggleTermsScrollButton(button, isVisible) {
        if (!button) {
            return;
        }

        button.disabled = !isVisible;
        button.classList.toggle('hidden', !isVisible);
        button.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    },

    renderFilters(chips) {
        if (!this.filtersSection || !this.filtersTrack) {
            return;
        }

        const chipsMarkup = chips
            .map(chip => {
                const key = escapeHtml(chip.key || '');
                const rawValue = escapeHtml(chip.raw_value || '');
                const label = escapeHtml(chip.value || '');

                return `<button class="inline-flex items-center gap-8 rounded-full bg-grey-900 px-8 py-[6px] text-1412 leading-none text-base-white transition-opacity hover:opacity-90 [&_i]:shrink-0" type="button" data-listing-active-filter-remove data-filter-key="${key}" data-filter-value="${rawValue}"><span>${label}</span><i class="icon icon-close text-[14px]!"></i></button>`;
            })
            .join('');

        this.filtersTrack.innerHTML = chipsMarkup;

        const inner = this.filtersSection.querySelector(
            '.listing__active-filters-inner'
        );
        let clearButton = this.filtersSection.querySelector(
            '[data-listing-filters-clear]'
        );

        if (!clearButton && chips.length > 0) {
            const clearMarkup =
                '<button class="text-1312 font-normal leading-[1.2] text-grey-900 underline underline-offset-[0.15em] hover:opacity-80" type="button" data-listing-filters-clear>Wyczyść wszystkie filtry</button>';
            const target = inner || this.filtersTrack;
            target.insertAdjacentHTML('beforeend', clearMarkup);
            clearButton = this.filtersSection.querySelector(
                '[data-listing-filters-clear]'
            );
        }

        if (clearButton) {
            clearButton.classList.toggle('hidden', chips.length === 0);
        }

        this.filtersSection.classList.toggle('hidden', chips.length === 0);

        const inlineWrapper = this.filtersSection.querySelector(
            '.listing__active-filters'
        );

        if (inlineWrapper) {
            inlineWrapper.classList.toggle('hidden', chips.length === 0);
        }
    },

    getEmptyResultsMessage() {
        if (this.isDesignersInspirationsView?.()) {
            return (
                this.bootstrap?.i18n?.emptyDesigners ||
                'Sekcja projektantów będzie dostępna wkrótce.'
            );
        }

        return this.bootstrap?.i18n?.emptyResults || 'Brak wyników.';
    },

    renderResults(results, pagination = {}, options = {}) {
        if (!this.results) {
            return;
        }

        const { append = false } = options;
        const items = Array.isArray(results) ? results : [];
        const markup = this.buildListingResultsMarkup(items);

        if (!markup && !append) {
            const emptyMessage = this.getEmptyResultsMessage();
            this.results.innerHTML = `<section class="listing__empty"><p>${escapeHtml(
                emptyMessage
            )}</p></section>`;
            return;
        }

        const grid = this.results.querySelector('[data-listing-results-grid]');

        if (append && grid) {
            if (markup) {
                grid.insertAdjacentHTML('beforeend', markup);
            }
        } else if (markup) {
            if (grid) {
                grid.innerHTML = markup;
            } else {
                const gridClass = this.isInspirationsMode()
                    ? INSPIRATIONS_RESULTS_GRID_CLASS
                    : LISTING_RESULTS_GRID_CLASS;

                this.results.innerHTML = `<div class="${gridClass}" data-listing-results-grid>${markup}</div>`;
            }
        } else if (!append) {
            const emptyMessage = this.getEmptyResultsMessage();
            this.results.innerHTML = `<section class="listing__empty"><p>${escapeHtml(
                emptyMessage
            )}</p></section>`;
            return;
        }

        this.renderResultsPagination(pagination, { append });
    },

    buildListingResultsMarkup(results) {
        return results
            .map(item => this.normalizeListingResultHtml(item))
            .filter(Boolean)
            .join('');
    },

    normalizeListingResultHtml(item) {
        if (!item || typeof item !== 'object') {
            return '';
        }

        const html = typeof item.html === 'string' ? item.html.trim() : '';

        if (!html || this.isEmptyListingResultHtml(html)) {
            return '';
        }

        if (this.hasListingResultWrapper(html)) {
            return html;
        }

        return `<div class="${LISTING_PRODUCT_WRAPPER_CLASS}">${html}</div>`;
    },

    hasListingResultWrapper(html) {
        if (this.isInspirationsMode?.()) {
            return (
                html.includes('js-inspiration-card') ||
                html.includes('js-gallery-item')
            );
        }

        return (
            html.includes(LISTING_PRODUCT_WRAPPER_CLASS) ||
            html.includes('listing__grid-block')
        );
    },

    isEmptyListingResultHtml(html) {
        const template = document.createElement('template');
        template.innerHTML = html;

        const nodes = Array.from(template.content.children);

        if (nodes.length === 0) {
            return template.content.textContent.trim() === '';
        }

        return nodes.every(node => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return node.textContent.trim() === '';
            }

            return node.children.length === 0 && node.textContent.trim() === '';
        });
    },

    renderResultsPagination(pagination = {}, options = {}) {
        const { append = false } = options;

        if (!this.results) {
            return;
        }

        const total = Number(
            pagination.total || this.total?.dataset.total || 0
        );
        const current = this.getDisplayedProductCount(
            total,
            Number(pagination.current || 0)
        );
        const maxPages = Number(pagination.maxPages || 1);
        const page = Number(pagination.page || this.state.page || 1);
        const hasMore = Boolean(pagination.hasMore);
        const progress = total > 0 ? Math.min(100, (current / total) * 100) : 0;

        let paginationRoot = this.results.querySelector(
            '[data-listing-pagination]'
        );

        if (!paginationRoot && total > 0) {
            const loadMoreLabel =
                this.bootstrap?.i18n?.loadMore || 'Pokaż więcej';
            this.results.insertAdjacentHTML(
                'beforeend',
                `<div class="flex flex-col items-center gap-32 pt-80 text-center" data-listing-pagination data-page="${page}" data-max-pages="${maxPages}" data-total="${total}" data-current="${current}"><p class="text-regular font-medium" data-listing-pagination-count></p><div class="relative block h-1 w-full bg-background-secondary desktop:w-106" data-listing-pagination-progress><div class="absolute left-0 top-0 h-1 bg-background-accent" data-listing-pagination-progress-bar></div></div><div ${
                    hasMore ? '' : 'class="hidden"'
                } data-listing-load-more><div><button name="" type="button" class="button-secondary button group">${escapeHtml(
                    loadMoreLabel
                )}</button></div></div></div>`
            );
            paginationRoot = this.results.querySelector(
                '[data-listing-pagination]'
            );
        }

        if (!paginationRoot) {
            return;
        }

        const count = paginationRoot.querySelector(
            '[data-listing-pagination-count]'
        );
        const progressBar = paginationRoot.querySelector(
            '[data-listing-pagination-progress-bar]'
        );
        const loadMore = paginationRoot.querySelector(
            '[data-listing-load-more]'
        );

        paginationRoot.dataset.page = String(page);
        paginationRoot.dataset.maxPages = String(maxPages);
        paginationRoot.dataset.total = String(total);
        paginationRoot.dataset.current = String(current);

        if (count) {
            const i18n = this.bootstrap?.i18n || {};
            const between = i18n.paginationBetween || 'z';
            const label = this.isInspirationsMode()
                ? i18n.resultsUnitInspirations || 'inspiracji'
                : i18n.resultsUnitProducts || 'produktów';
            count.textContent = `${current.toLocaleString(
                'pl-PL'
            )} ${between} ${total.toLocaleString('pl-PL')} ${label}`;
        }

        if (progressBar) {
            progressBar.style.width = `${progress}%`;
        }

        if (loadMore) {
            loadMore.classList.toggle('hidden', !hasMore);
        }

        this.resultsPagination = paginationRoot;
        this.state.page = page;
    },

    setLoadMoreLoading(isLoading) {
        const loadMore =
            this.results?.querySelector('[data-listing-load-more]') ||
            this.root.querySelector('[data-listing-load-more]');
        const button = loadMore?.querySelector('button');

        if (!button) {
            return;
        }

        button.disabled = isLoading;
        button.classList.toggle('pointer-events-none', isLoading);
        button.classList.toggle('opacity-50', isLoading);
    },

    toggleSortMenu() {
        if (!this.sortMenu || !this.sortToggle) {
            return;
        }

        const isOpen = !this.sortMenu.classList.contains('hidden');

        if (isOpen) {
            this.closeSortMenu();
            return;
        }

        this.sortMenu.classList.remove('hidden');
        this.sortToggle.setAttribute('aria-expanded', 'true');
        this.sortIcon?.classList.add('rotate-180');
    },

    closeSortMenu() {
        if (!this.sortMenu || !this.sortToggle) {
            return;
        }

        this.sortMenu.classList.add('hidden');
        this.sortToggle.setAttribute('aria-expanded', 'false');
        this.sortIcon?.classList.remove('rotate-180');
    },

    getSortOptionElements() {
        const options = [
            ...this.root.querySelectorAll('[data-listing-sort-option]'),
            ...(this.filtersSidebar?.content?.querySelectorAll(
                '[data-listing-sort-option]'
            ) || []),
        ];

        return Array.from(new Set(options));
    },

    updateSortUi(sort) {
        this.state.sort = sort || 'popular';

        this.getSortOptionElements().forEach(option => {
            const isActive = option.dataset.sortValue === this.state.sort;
            const isInSidebar = Boolean(
                this.filtersSidebar?.content?.contains(option)
            );
            const indicator = option.querySelector(
                'span.flex > span, span span'
            );
            const outerIndicator = option.querySelector('span.flex');

            option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            option.classList.toggle('border-transparent', true);
            option.classList.toggle('bg-white', isActive);
            option.classList.remove('hover:border-grey-900');

            if (!isInSidebar) {
                option.classList.toggle('hover:border-grey-900', !isActive);
            }

            option.classList.remove(
                'border-grey-900',
                'bg-beige-50',
                'bg-beige-200'
            );

            if (indicator) {
                indicator.classList.toggle('bg-orange-500', isActive);
                indicator.classList.toggle('bg-transparent', !isActive);
            }

            if (outerIndicator) {
                outerIndicator.classList.toggle('border-orange-500', isActive);
                outerIndicator.classList.toggle('border-grey-900', !isActive);
            }
        });
    },

    getDisplayedProductCount(total = 0, fallbackCount = null) {
        const counted = this.countDisplayedResults();
        const base = counted > 0 ? counted : Number(fallbackCount || 0);

        if (total > 0) {
            return Math.min(base, total);
        }

        return base;
    },

    countDisplayedResults() {
        const grid = this.results?.querySelector('[data-listing-results-grid]');

        if (!grid) {
            return 0;
        }

        return Array.from(grid.children).filter(
            element => !this.isListingGridBlockElement(element)
        ).length;
    },

    isListingGridBlockElement(element) {
        if (!element || element.nodeType !== Node.ELEMENT_NODE) {
            return false;
        }

        return (
            element.classList.contains('listing__grid-block-spacer-wrapper') ||
            element.classList.contains('listing__grid-block') ||
            element.hasAttribute('data-listing-grid-block') ||
            Boolean(
                element.querySelector(
                    '[data-listing-grid-block], .listing__grid-block'
                )
            )
        );
    }
};
